import React from "react";
import { Box, Tab, Tabs, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { tokens } from "../../theme";

export interface PillTabItem<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactElement;
}

interface PillTabsProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  items: PillTabItem<T>[];
  ariaLabel?: string;
  /** Stretch to the container on phones (tabs share the width equally). */
  fullWidthOnPhone?: boolean;
}

/** Glass segmented tabs (same look as the LPP view switcher). */
export function PillTabs<T extends string>({ value, onChange, items, ariaLabel, fullWidthOnPhone = true }: PillTabsProps<T>) {
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const fill = fullWidthOnPhone && isPhone;
  return (
    <Box
      sx={{
        display: fill ? "flex" : "inline-flex",
        width: fill ? "100%" : "auto",
        maxWidth: "100%",
        p: "5px",
        borderRadius: 999,
        bgcolor: "rgba(18,18,20,0.72)",
        backdropFilter: "blur(22px) saturate(160%)",
        WebkitBackdropFilter: "blur(22px) saturate(160%)",
        border: `1px solid ${tokens.glass.border}`,
        boxShadow: `${tokens.glass.highlight}, 0 18px 40px -18px rgba(0,0,0,0.95)`,
      }}
    >
      <Tabs
        value={value}
        onChange={(_, v: T) => onChange(v)}
        variant={fill ? "fullWidth" : "scrollable"}
        scrollButtons={false}
        textColor="inherit"
        aria-label={ariaLabel}
        sx={{
          minHeight: 0,
          flex: fill ? 1 : undefined,
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTabs-flexContainer": { gap: 0.5 },
          "& .MuiTabs-scroller": { borderRadius: 999 },
        }}
      >
        {items.map((item) => (
          <Tab
            key={item.value}
            value={item.value}
            label={item.label}
            icon={item.icon}
            iconPosition="start"
            disableRipple
            sx={{
              minHeight: 36,
              height: 36,
              "@media (pointer: coarse)": { minHeight: 44, height: 44 },
              px: { xs: 1.5, md: 2 },
              gap: 0.75,
              borderRadius: 999,
              fontSize: "0.8125rem",
              fontWeight: 650,
              color: "text.secondary",
              "& .MuiTab-iconWrapper": { m: 0, fontSize: 17 },
              transition: "background-color 140ms ease, color 140ms ease",
              "&:hover": { color: "text.primary", bgcolor: "rgba(255,255,255,0.05)" },
              "&.Mui-selected": {
                color: "text.primary",
                bgcolor: "rgba(255,255,255,0.13)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14)",
              },
              "&.Mui-focusVisible": { outline: `2px solid ${tokens.gold}`, outlineOffset: -2 },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}

export default PillTabs;
